/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useEffect, useState } from 'react';
import Bread from 'modules/layoutModule/components/Bread/Bread';
import { HomeOutlined, EditOutlined, DeleteFilled } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import {
  Breadcrumb,
  Pagination,
  Popconfirm,
  ConfigProvider,
  BackTop,
} from 'antd';
import { Table, Space, Tooltip, Button } from 'antd';
import TableDateRow from 'modules/layoutModule/components/TableDateRow/TableDateRow';
import {
  getPlantsScheduleRequest,
  removeSpecificFertilizationScheduleRequest,
} from 'modules/knowledgeModule/requests/knowledgeRequests';
import plPL from 'antd/lib/locale/pl_PL';
import moment from 'moment';
import TableDataRowTypes from 'modules/layoutModule/enums/TableDataRowTypes';

export interface NewsTableTypes {
  id: number;
  dateAdd: moment.MomentInput;
  name: string;
}

const FertilizationSchedule = (): JSX.Element => {
  const [tableData, setTableData] = useState<NewsTableTypes[]>([]);
  const [loading, toggleLoading] = useState<boolean>(true);

  const confirmDelete = (id: number) =>
    removeSpecificFertilizationScheduleRequest(id).then(() =>
      downloadFertilization()
    );

  const downloadFertilization = () =>
    getPlantsScheduleRequest().then((response) => {
      if (response) {
        const { data } = response;
        setTableData(data);
        toggleLoading(false);
      } else {
        toggleLoading(false);
      }
    });

  useEffect(() => {
    if (tableData.length === 0) {
      downloadFertilization();
    }
  }, [tableData]);

  const columns = [
    {
      title: 'Nazwa',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: any) => (
        <TableDateRow
          record={record}
          field={TableDataRowTypes.NAME_LINK}
          link={`wiedza/harmonogram-nawozenia/${record.url}`}
        />
      ),
    },
    {
      title: 'Piktogram biały',
      dataIndex: 'fileImage2',
      key: 'fileImage2',
      width: 100,
      render: (_: any, record: any) => (
        <TableDateRow
          record={record}
          field={TableDataRowTypes.CUSTOM_IMAGE}
          fieldName="fileImage2"
          size={80}
        />
      ),
    },
    {
      title: 'Okładka wszystkie',
      dataIndex: 'fileImageAll',
      key: 'fileImageAll',
      width: 100,
      render: (_: any, record: any) => (
        <TableDateRow
          record={record}
          field={TableDataRowTypes.IMAGE}
          fieldName="fileImageAll"
          size={100}
          customHeight
        />
      ),
    },
    {
      title: 'Powiązany z',
      dataIndex: 'plants',
      key: 'plants',
      width: 140,
      sorter: (a: any, b: any) => a.plants - b.plants,
      render: (_: any, record: any) => <>{record.plantName || '-'}</>,
    },
    {
      title: 'Kolejność',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 100,
      sorter: (a: any, b: any) => a.sortOrder - b.sortOrder,
      render: (text: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.SORT} />
      ),
    },
    {
      title: 'Licznik',
      dataIndex: 'viewCount',
      width: 100,
      sorter: (a: { viewCount: number }, b: { viewCount: number }) =>
        a.viewCount - b.viewCount,
      render: (text: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.VIEWS} />
      ),
    },
    {
      title: 'Aktywna',
      dataIndex: 'isActive',
      key: 'isActive',
      width: '150px',
      render: (
        text: string,
        record: {
          type: number;
          dateAdd: Date;
          id: number;
          isActive: number | boolean;
          taste: number;
          colour: number;
          category?: string | undefined;
          url: string | undefined;
        }
      ) => <TableDateRow record={record} field={TableDataRowTypes.IS_ACTIVE} />,
      sorter: (a: { isActive: number }, b: { isActive: number }) =>
        a.isActive - b.isActive,
      filters: [
        {
          text: 'TAK',
          value: 'isActive',
        },
        {
          text: 'NIE',
          value: 'isNotActive',
        },
      ],
      onFilter: (value: string, record: { isActive: boolean }) => {
        if (value === 'isActive') {
          return record.isActive;
        }
        return !record.isActive;
      },
    },
    {
      title: 'Akcje',
      dataIndex: 'operation',
      key: 'operation',
      width: 100,
      render: (
        text: string,
        item: { id: number; deleteDisabled: boolean; isActive: number }
      ) => {
        return (
          <Space size="middle">
            <Tooltip title="Edytuj wpis">
              <Link to={`/fertilization-schedule/${item.id}`}>
                <Button type="primary" shape="circle" icon={<EditOutlined />} />
              </Link>
            </Tooltip>
            <Tooltip title="Usuń wpis">
              <Popconfirm
                title="Czy na pewno chcesz usunąc ten wpis?"
                placement="left"
                onConfirm={() => confirmDelete(item.id)}
                okText="Tak"
                cancelText="Nie"
                disabled={item.isActive === 1}
              >
                <Button type="link" disabled={item.isActive === 1}>
                  <DeleteFilled />
                </Button>
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Bread>
        <>
          <Breadcrumb.Item>
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Wiedza</Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/fertilization-schedule">Harmonogramy</Link>
          </Breadcrumb.Item>
        </>
      </Bread>
      <ConfigProvider locale={plPL}>
        <Table
          // @ts-ignore
          columns={columns}
          // @ts-ignore
          dataSource={tableData}
          loading={loading}
          rowKey={(record) => record.id}
        >
          <Pagination />
        </Table>
        <BackTop />
      </ConfigProvider>
    </>
  );
};

export default FertilizationSchedule;
